import type { ContentI18n } from "@/lib/questContent/types";
import type {
  IAdminService,
  AdminPaginationFilters,
  AdminProfileInfo,
  AdminMapPoint,
} from "@/services/interfaces/IAdminService";
import type {
  AdminWorkItemKPIs,
  AdminQuestFilters,
  AdminTaskFilters,
  AdminUserFilters,
  AdminUserSummary,
  AdminAgentSummary,
  AdminUserDetail,
  AdminNotificationChannel,
  AdminChannelMutationResult,
  UserProfile,
  UserRole,
  ActivityLevel,
  Quest,
  Task,
  WorkSubmission,
  NegotiationThread,
  PriceProposal,
  PaginatedResult,
  OnchainTxRow,
  OnchainTxFilters,
  OnchainTxInsert,
  OnchainReceiptPatch,
  OnchainContractKey,
  OnchainTxStatus,
  OnchainLinkedWallet,
  UserTier,
} from "@/lib/types";
import { toSupportedLanguage } from "@/lib/types";
import { toUserTier } from "@/lib/tier";
import { supabase } from "@/lib/supabase";
import { ADMIN_STALE_THRESHOLD_DAYS, ADMIN_PAGE_SIZE } from "@/lib/constants";
import { assertAdmin } from "./adminGuard";
import { mapTaskTrustColumns, mapTrustGradeStatsRow, type TaskTrustRow } from "@/lib/trustGrade/mapRow";
import type { TrustGrade, TrustGradeStatsRow } from "@/lib/trustGrade/types";

// ─── Row types ──────────────────────────────────────────

interface QuestRow {
  id: string;
  creator_id: string;
  title: string;
  description: string;
  category: string;
  status: string;
  total_budget_usd: number;
  currency: string;
  location_requirement: string | null;
  deadline: string | null;
  created_at: string;
  updated_at: string;
  chain_quest_id: number | null;
  chain_tx_hash: string | null;
  /** DEV-824: machine-written translations, carried so admin-listed quests
   *  read in the admin's language just like every other surface. */
  i18n: ContentI18n | null;
  source_lang: string | null;
  tasks?: TaskRow[];
}

interface TaskRow extends TaskTrustRow {
  id: string;
  quest_id: string;
  parent_task_id: string | null;
  title: string;
  description: string;
  task_type: string;
  status: string;
  budget_usd: number;
  estimated_effort_hours: number;
  deadline: string | null;
  skills: string[];
  assignee_id: string | null;
  order: number;
  created_at: string;
  updated_at: string;
  work_mode: string | null;
  location_countries: string[] | null;
  location_cities: string[] | null;
  chain_task_id: number | null;
  chain_tx_hash: string | null;
  chain_assigned: boolean;
}

interface SubmissionRow {
  id: string;
  task_id: string;
  submitter_id: string;
  description: string;
  attachments: unknown[];
  submitted_at: string;
  status: string;
  feedback: string | null;
}

interface ProposalRow {
  id: string;
  task_id: string;
  quest_id: string;
  worker_id: string;
  proposed_price: number;
  message: string | null;
  status: string;
  parent_proposal_id: string | null;
  round: number;
  proposed_by: string;
  created_at: string;
  updated_at: string;
}

// ─── Row mappers ────────────────────────────────────────

function mapQuestRow(row: QuestRow): Quest {
  const tasks = row.tasks ?? [];
  return {
    id: row.id,
    creatorId: row.creator_id,
    title: row.title,
    description: row.description,
    category: row.category,
    status: row.status as Quest["status"],
    totalBudgetUSD: Number(row.total_budget_usd),
    currency: row.currency as Quest["currency"],
    locationRequirement: (row.location_requirement as Quest["locationRequirement"]) ?? "remote",
    deadline: row.deadline,
    attachments: [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    taskCount: tasks.length,
    completedTaskCount: tasks.filter((t) => t.status === "completed").length,
    chainQuestId: row.chain_quest_id ?? null,
    chainTxHash: row.chain_tx_hash ?? null,
    i18n: row.i18n ?? null,
    sourceLang: row.source_lang ?? null,
  };
}

function mapTaskRow(row: TaskRow): Task {
  return {
    id: row.id,
    questId: row.quest_id,
    parentTaskId: row.parent_task_id,
    title: row.title,
    description: row.description,
    taskType: row.task_type as Task["taskType"],
    status: row.status as Task["status"],
    budgetUSD: Number(row.budget_usd),
    estimatedEffortHours: Number(row.estimated_effort_hours),
    deadline: row.deadline,
    skills: row.skills ?? [],
    workMode: (row.work_mode as Task["workMode"]) ?? "remote",
    locationCountries: row.location_countries ?? [],
    locationCities: row.location_cities ?? [],
    assigneeId: row.assignee_id,
    order: row.order,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    chainTaskId: row.chain_task_id ?? null,
    chainTxHash: row.chain_tx_hash ?? null,
    chainAssigned: row.chain_assigned ?? false,
    ...mapTaskTrustColumns(row),
  };
}

function mapSubmissionRow(row: SubmissionRow): WorkSubmission {
  return {
    id: row.id,
    taskId: row.task_id,
    submitterId: row.submitter_id,
    description: row.description,
    attachments: [],
    submittedAt: row.submitted_at,
    status: row.status as WorkSubmission["status"],
    feedback: row.feedback,
  };
}

function mapProposalRow(row: ProposalRow): PriceProposal {
  return {
    id: row.id,
    taskId: row.task_id,
    questId: row.quest_id,
    workerId: row.worker_id,
    proposedPrice: Number(row.proposed_price),
    message: row.message,
    status: row.status as PriceProposal["status"],
    parentProposalId: row.parent_proposal_id,
    round: row.round,
    proposedBy: row.proposed_by as PriceProposal["proposedBy"],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

// ─── Service ────────────────────────────────────────────

export class SupabaseAdminService implements IAdminService {
  async resolveProfiles(
    userIds: string[],
  ): Promise<Map<string, AdminProfileInfo>> {
    const map = new Map<string, AdminProfileInfo>();
    const unique = [...new Set(userIds.filter(Boolean))];
    if (unique.length === 0) return map;
    await assertAdmin();

    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, email")
      .in("id", unique);

    if (error) return map; // graceful fallback

    for (const row of data ?? []) {
      map.set(row.id, {
        displayName: row.display_name || row.email || row.id,
        email: row.email || "",
      });
    }
    return map;
  }

  async resolveTaskTitles(
    taskIds: string[],
  ): Promise<Map<string, string>> {
    const map = new Map<string, string>();
    const unique = [...new Set(taskIds.filter(Boolean))];
    if (unique.length === 0) return map;
    await assertAdmin();

    const { data, error } = await supabase
      .from("tasks")
      .select("id, title")
      .in("id", unique);

    if (error) return map; // graceful fallback

    for (const row of data ?? []) {
      map.set(row.id, row.title);
    }
    return map;
  }

  async getWorkItemKPIs(chainId?: number): Promise<AdminWorkItemKPIs> {
    await assertAdmin();
    // When chain-scoped, get quest IDs first (small set — just IDs)
    let questIdFilter: string[] | null = null;
    if (chainId) {
      const { data: cq, error: cqErr } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", chainId);
      if (cqErr) throw new Error(`Failed to load quest KPIs: ${cqErr.message}`);
      questIdFilter = (cq ?? []).map((q) => q.id);
      if (questIdFilter.length === 0) return emptyKPIs();
    }

    const staleThreshold = new Date(
      Date.now() - ADMIN_STALE_THRESHOLD_DAYS * 86_400_000,
    ).toISOString();

    // Build quest count queries (head: true — no rows transferred)
    const qTotal = supabase.from("quests").select("id", { count: "exact", head: true });
    const qActive = supabase.from("quests").select("id", { count: "exact", head: true }).eq("status", "active");
    if (chainId) {
      qTotal.eq("chain_id", chainId);
      qActive.eq("chain_id", chainId);
    }

    // Build task count queries
    const tTotal = supabase.from("tasks").select("id", { count: "exact", head: true });
    const tOpen = supabase.from("tasks").select("id", { count: "exact", head: true }).eq("status", "open");
    const tUnassigned = supabase.from("tasks").select("id", { count: "exact", head: true }).eq("status", "open").is("assignee_id", null);
    const tStale = supabase.from("tasks").select("id", { count: "exact", head: true }).in("status", ["assigned", "in_progress"]).lt("updated_at", staleThreshold);
    const tPending = supabase.from("tasks").select("id", { count: "exact", head: true }).eq("status", "submitted");
    const tDisputed = supabase.from("tasks").select("id", { count: "exact", head: true }).eq("status", "disputed");
    const tCompleted = supabase.from("tasks").select("id", { count: "exact", head: true }).eq("status", "completed");

    if (questIdFilter) {
      tTotal.in("quest_id", questIdFilter);
      tOpen.in("quest_id", questIdFilter);
      tUnassigned.in("quest_id", questIdFilter);
      tStale.in("quest_id", questIdFilter);
      tPending.in("quest_id", questIdFilter);
      tDisputed.in("quest_id", questIdFilter);
      tCompleted.in("quest_id", questIdFilter);
    }

    // Fire all counts in parallel — no rows transferred, only counts
    const [
      totalQuestsR, activeQuestsR,
      totalTasksR, openTasksR, unassignedTasksR, staleTasksR,
      pendingReviewTasksR, disputedTasksR, completedTasksR,
    ] = await Promise.all([
      qTotal, qActive,
      tTotal, tOpen, tUnassigned, tStale,
      tPending, tDisputed, tCompleted,
    ]);

    const c = (r: { count: number | null }) => r.count ?? 0;
    const totalTasks = c(totalTasksR);
    const completedTasks = c(completedTasksR);

    // User counts via RPC
    let totalUsers = 0;
    let activeUsers = 0;
    if (chainId) {
      const { data: ucData } = await supabase.rpc("admin_count_users_by_chain", {
        p_chain_id: chainId,
      });
      if (ucData && ucData.length > 0) {
        totalUsers = Number(ucData[0].total_users);
        activeUsers = Number(ucData[0].active_users);
      }
    }

    return {
      totalQuests: c(totalQuestsR),
      activeQuests: c(activeQuestsR),
      totalTasks,
      openTasks: c(openTasksR),
      unassignedTasks: c(unassignedTasksR),
      staleTasks: c(staleTasksR),
      pendingReviewTasks: c(pendingReviewTasksR),
      disputedTasks: c(disputedTasksR),
      completionRate:
        totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0,
      totalUsers,
      activeUsers,
    };
  }

  async listAllQuests(
    filters: AdminQuestFilters,
  ): Promise<PaginatedResult<Quest>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const offset = Number.isNaN(currentOffset) ? 0 : currentOffset;

    // noLocation requires a subquery (location_cities lives on tasks, not quests)
    if (filters.noLocation) {
      const search = filters.search?.replace(/[%_\\]/g, "\\$&") ?? null;
      const { data: rpcData, error } = await supabase.rpc("admin_list_quests_no_location", {
        p_chain_id: filters.chainId ?? null,
        p_limit: limit,
        p_offset: offset,
        p_search: search,
      });
      if (error) throw new Error(`Failed to list quests: ${error.message}`);
      const result = rpcData as { data: unknown[]; total: number; has_more: boolean };
      type RpcRow = { id: string; title: string; status: string; chain_id: number | null; creator_id: string; created_at: string; total_budget_usd: number; task_count: number; completed_task_count: number };
      const quests: Quest[] = (result.data as RpcRow[]).map((r) => ({
        id: r.id,
        creatorId: r.creator_id,
        title: r.title,
        description: "",
        category: "",
        status: r.status as Quest["status"],
        totalBudgetUSD: Number(r.total_budget_usd),
        currency: "USDC",
        locationRequirement: "remote",
        deadline: null,
        attachments: [],
        createdAt: r.created_at,
        updatedAt: r.created_at,
        taskCount: r.task_count,
        completedTaskCount: r.completed_task_count,
        chainQuestId: null,
        chainTxHash: null,
      }));
      const nextOffset = offset + limit;
      return {
        data: quests,
        total: result.total,
        cursor: result.has_more ? String(nextOffset) : null,
        hasMore: result.has_more,
      };
    }

    let query = supabase
      .from("quests")
      .select("*, tasks(id, status)", { count: "exact" })
      .order("created_at", { ascending: false })
      .limit(limit);

    if (filters.chainId) {
      query = query.eq("chain_id", filters.chainId);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.search) {
      const escaped = filters.search.replace(/[%_\\]/g, "\\$&");
      query = query.ilike("title", `%${escaped}%`);
    }
    if (filters.cursor) {
      query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list quests: ${error.message}`);

    const rows = (data ?? []) as QuestRow[];
    const quests = rows.map(mapQuestRow);
    const total = count ?? quests.length;
    const nextOffset = offset + limit;

    return {
      data: quests,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async listAllTasks(
    filters: AdminTaskFilters,
  ): Promise<PaginatedResult<Task>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;

    // When chain-scoped, first get quest IDs for that chain
    let questIdFilter: string[] | null = null;
    if (filters.chainId) {
      const { data: chainQuests, error: cqErr } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", filters.chainId);
      if (cqErr) throw new Error(`Failed to filter by chain: ${cqErr.message}`);
      questIdFilter = (chainQuests ?? []).map((q) => q.id);
      if (questIdFilter.length === 0) {
        return { data: [], total: 0, cursor: null, hasMore: false };
      }
    }

    let query = supabase
      .from("tasks")
      .select("*", { count: "exact" })
      .order("updated_at", { ascending: false })
      .limit(limit);

    if (questIdFilter) {
      query = query.in("quest_id", questIdFilter);
    }
    if (filters.status) {
      query = query.eq("status", filters.status);
    }
    if (filters.taskType) {
      query = query.eq("task_type", filters.taskType);
    }
    if (filters.trustGrade !== undefined) query = query.eq("trust_grade", filters.trustGrade);
    if (filters.specialSuggestedOnly) query = query.eq("trust_special_suggested", true).is("trust_grade_override", null);
    if (filters.trustSource) query = query.eq("trust_factors_source", filters.trustSource);
    if (filters.assigneePresence === "unassigned") {
      query = query.is("assignee_id", null);
    } else if (filters.assigneePresence === "assigned") {
      query = query.not("assignee_id", "is", null);
    }
    if (filters.staleOnly) {
      const threshold = new Date(
        Date.now() - ADMIN_STALE_THRESHOLD_DAYS * 86_400_000,
      ).toISOString();
      query = query
        .in("status", ["assigned", "in_progress"])
        .lt("updated_at", threshold);
    }
    if (filters.cursor) {
      const offset = parseInt(filters.cursor, 10);
      if (!Number.isNaN(offset)) query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list tasks: ${error.message}`);

    const rows = (data ?? []) as TaskRow[];
    const tasks = rows.map(mapTaskRow);
    const total = count ?? tasks.length;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const nextOffset = (Number.isNaN(currentOffset) ? 0 : currentOffset) + limit;

    return {
      data: tasks,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async setTaskTrustGrade(taskId: string, grade: TrustGrade | null, reason: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_set_task_trust_grade", {
      p_task_id: taskId,
      p_grade: grade,
      p_reason: reason,
    });
    if (error) throw new Error(`Failed to set trust grade: ${error.message}`);
  }

  async setUserTier(userId: string, tier: UserTier): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_set_user_tier", { p_user: userId, p_tier: tier });
    if (error) throw new Error(`Failed to set tier: ${error.message}`);
  }

  async getTrustGradeStats(chainId: number | null): Promise<TrustGradeStatsRow[]> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_trust_grade_stats", { p_chain_id: chainId });
    if (error) throw new Error(`Failed to load trust grade stats: ${error.message}`);
    return ((data ?? []) as Record<string, unknown>[]).map(mapTrustGradeStatsRow);
  }

  async listPendingReviews(
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<WorkSubmission>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;

    let query = supabase
      .from("submissions")
      .select("*", { count: "exact" })
      .eq("status", "pending")
      .order("submitted_at", { ascending: true })
      .limit(limit);

    // Chain-scope: filter by tasks belonging to quests on the target chain
    if (filters.chainId) {
      const { data: chainQuests } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", filters.chainId);
      const questIds = (chainQuests ?? []).map((q) => q.id);
      if (questIds.length === 0) {
        return { data: [], total: 0, cursor: null, hasMore: false };
      }
      const { data: chainTasks } = await supabase
        .from("tasks")
        .select("id")
        .in("quest_id", questIds);
      const taskIds = (chainTasks ?? []).map((t) => t.id);
      if (taskIds.length === 0) {
        return { data: [], total: 0, cursor: null, hasMore: false };
      }
      query = query.in("task_id", taskIds);
    }

    if (filters.cursor) {
      const offset = parseInt(filters.cursor, 10);
      if (!Number.isNaN(offset)) query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list pending reviews: ${error.message}`);

    const rows = (data ?? []) as SubmissionRow[];
    const submissions = rows.map(mapSubmissionRow);
    const total = count ?? submissions.length;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const nextOffset = (Number.isNaN(currentOffset) ? 0 : currentOffset) + limit;

    return {
      data: submissions,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async listStuckNegotiations(
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<NegotiationThread>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.cursor ? parseInt(filters.cursor, 10) || 0 : 0;

    // Build chain-scope filter
    let questIdFilter: string[] | null = null;
    if (filters.chainId) {
      const { data: chainQuests } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", filters.chainId);
      const questIds = (chainQuests ?? []).map((q) => q.id);
      if (questIds.length === 0) {
        return { data: [], total: 0, cursor: null, hasMore: false };
      }
      questIdFilter = questIds;
    }

    // Step 1: Get distinct (task_id, worker_id) pairs that have pending proposals.
    // Fetch only the identifying columns, ordered by latest activity.
    let pendingQuery = supabase
      .from("proposals")
      .select("task_id, worker_id, proposed_price, updated_at")
      .eq("status", "pending")
      .order("updated_at", { ascending: true });
    if (questIdFilter) pendingQuery = pendingQuery.in("quest_id", questIdFilter);

    const { data: pendingRows, error: pErr } = await pendingQuery;
    if (pErr) throw new Error(`Failed to list negotiations: ${pErr.message}`);

    // Deduplicate into unique threads, keeping only the latest proposal info
    const threadKeys = new Map<string, { taskId: string; workerId: string; latestPrice: number }>();
    for (const row of pendingRows ?? []) {
      const key = `${row.task_id}:${row.worker_id}`;
      // Last write wins since rows are ordered by updated_at asc
      threadKeys.set(key, {
        taskId: row.task_id,
        workerId: row.worker_id,
        latestPrice: Number(row.proposed_price),
      });
    }

    const allThreadKeys = [...threadKeys.values()];
    const total = allThreadKeys.length;
    const pageKeys = allThreadKeys.slice(offset, offset + limit);

    if (pageKeys.length === 0) {
      return { data: [], total, cursor: null, hasMore: false };
    }

    // Step 2: For the page of threads, fetch their full proposal history
    const taskIds = [...new Set(pageKeys.map((k) => k.taskId))];
    const workerIds = [...new Set(pageKeys.map((k) => k.workerId))];

    let detailQuery = supabase
      .from("proposals")
      .select("*")
      .in("task_id", taskIds)
      .in("worker_id", workerIds)
      .order("created_at", { ascending: true });

    const { data: detailRows, error: dErr } = await detailQuery;
    if (dErr) throw new Error(`Failed to load proposal details: ${dErr.message}`);

    const proposals = ((detailRows ?? []) as ProposalRow[]).map(mapProposalRow);

    // Group into threads
    const threadMap = new Map<string, NegotiationThread>();
    for (const p of proposals) {
      const key = `${p.taskId}:${p.workerId}`;
      if (!threadKeys.has(key)) continue; // not in our page
      let thread = threadMap.get(key);
      if (!thread) {
        thread = {
          taskId: p.taskId,
          workerId: p.workerId,
          workerName: p.workerId,
          workerAvatarUrl: null,
          proposals: [],
          currentStatus: p.status,
          latestPrice: p.proposedPrice,
          agreedPrice: null,
        };
        threadMap.set(key, thread);
      }
      thread.proposals.push(p);
      thread.latestPrice = p.proposedPrice;
      thread.currentStatus = p.status;
    }

    const threads = pageKeys.map((k) => {
      const key = `${k.taskId}:${k.workerId}`;
      return threadMap.get(key)!;
    }).filter(Boolean);

    const nextOffset = offset + limit;
    return {
      data: threads,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  // ─── User management ──────────────────────────────────

  async listUsers(
    filters: AdminUserFilters,
  ): Promise<PaginatedResult<AdminUserSummary>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;
    const offset = filters.cursor ? parseInt(filters.cursor, 10) || 0 : 0;

    if (filters.noLocation) {
      let query = supabase
        .from("profiles")
        .select("id, display_name, handle, email, avatar_url, role_preference, role, created_at", { count: "exact" })
        .or("location_country.is.null,location_city.is.null")
        .order("created_at", { ascending: false })
        .range(offset, offset + limit - 1);

      if (filters.search) {
        const escaped = filters.search.replace(/[%_\\]/g, "\\$&");
        query = query.ilike("display_name", `%${escaped}%`);
      }

      const { data, error, count } = await query;
      if (error) throw new Error(`Failed to list users: ${error.message}`);

      const rows = (data ?? []) as Record<string, unknown>[];
      const total = count ?? rows.length;
      const nextOffset = offset + limit;

      return {
        data: await withTiers(rows.map((row) => ({
          id: row.id as string,
          displayName: (row.display_name as string) || (row.handle as string) || (row.id as string),
          handle: (row.handle as string) || "",
          email: (row.email as string) || "",
          avatarUrl: (row.avatar_url as string | null) ?? null,
          rolePreference: (row.role_preference ?? "both") as AdminUserSummary["rolePreference"],
          role: (row.role ?? "user") as AdminUserSummary["role"],
          createdAt: row.created_at as string,
          // The location-gap view answers "whose profile is incomplete", and
          // asks no chain: there is nothing to count against. Zeroes here mean
          // "not counted", not "none" — which is why this branch renders the
          // same columns and the operator switches off the filter to see them.
          questCount: 0,
          taskCount: 0,
          proposalCount: 0,
          applicationCount: 0,
          // Same reason as the counts: this branch asks no chain and joins no
          // leads, so it reports "not looked up" rather than "signed up".
          leadSource: null,
          leadClaimedAt: null,
        }))),
        total,
        cursor: nextOffset < total ? String(nextOffset) : null,
        hasMore: nextOffset < total,
      };
    }

    if (!filters.chainId) {
      return { data: [], total: 0, cursor: null, hasMore: false };
    }

    const search = filters.search
      ? filters.search.replace(/[%_\\]/g, "\\$&")
      : null;

    const { data, error } = await supabase.rpc("admin_list_users_by_chain", {
      p_chain_id: filters.chainId,
      p_search: search,
      p_limit: limit + 1, // fetch one extra to detect hasMore
      p_offset: offset,
      // "all" is the absence of a filter, not a third value the RPC knows —
      // it rejects anything but 'lead' | 'signup' | NULL rather than widening
      // a typo back to everyone.
      p_origin: filters.origin && filters.origin !== "all" ? filters.origin : null,
      p_lead_source: filters.origin === "lead" ? filters.leadSource ?? null : null,
      // Sent only when on. PostgREST matches an RPC by its argument names, so
      // leaving it out keeps the default list working against a database that
      // predates 20260930120000, whichever of the two deploys first.
      ...(filters.activeOnly ? { p_active_only: true } : {}),
      // Same rule: only when filtering, so an unfiltered list works before the
      // migration that adds p_tier is applied.
      ...(filters.tier ? { p_tier: filters.tier } : {}),
    });

    if (error) throw new Error(`Failed to list users: ${error.message}`);

    const rows = data ?? [];
    const hasMore = rows.length > limit;
    const page = hasMore ? rows.slice(0, limit) : rows;

    const users: AdminUserSummary[] = page.map((row: Record<string, unknown>) => ({
      id: row.id as string,
      displayName: (row.display_name as string) || (row.handle as string) || (row.id as string),
      handle: (row.handle as string) || "",
      email: (row.email as string) || "",
      avatarUrl: (row.avatar_url as string | null) ?? null,
      rolePreference: (row.role_preference ?? "both") as AdminUserSummary["rolePreference"],
      role: (row.role ?? "user") as AdminUserSummary["role"],
      createdAt: row.created_at as string,
      questCount: Number(row.quest_count),
      taskCount: Number(row.task_count),
      proposalCount: Number(row.proposal_count),
      applicationCount: Number(row.application_count ?? 0),
      leadSource: (row.lead_source as AdminUserSummary["leadSource"]) ?? null,
      leadClaimedAt: (row.lead_claimed_at as string | null) ?? null,
    }));
    const usersWithTiers = await withTiers(users);

    // The RPC counts the filtered set before its LIMIT (`COUNT(*) OVER ()`),
    // so the pager shows a real total instead of the old
    // `offset + rows + (hasMore ? 1 : 0)` approximation, which climbed by one
    // every time an admin paged. `?? ` covers a deployment where the frontend
    // is ahead of the migration: an absent column falls back to the old
    // estimate rather than rendering a total of 0 with rows on screen.
    const reported = page.length > 0 ? Number((page[0] as Record<string, unknown>).total_count ?? NaN) : 0;
    const total = Number.isFinite(reported)
      ? reported
      : offset + page.length + (hasMore ? 1 : 0);
    const nextOffset = offset + limit;

    return {
      data: usersWithTiers,
      total,
      cursor: hasMore ? String(nextOffset) : null,
      hasMore,
    };
  }

  async getUserDetail(userId: string, chainId?: number): Promise<AdminUserDetail> {
    await assertAdmin();
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) throw new Error(`Failed to load user: ${error.message}`);

    const row = data as Record<string, unknown>;

    // Who last set the tier. A second read rather than an embed, so this page
    // keeps working before the migration adds tier_set_by.
    let tierSetByName: string | null = null;
    if (row.tier_set_by) {
      const { data: setter } = await supabase
        .from("profiles").select("display_name").eq("id", row.tier_set_by as string).maybeSingle();
      tierSetByName = (setter?.display_name as string | null | undefined) ?? null;
    }

    const profile: UserProfile = {
      id: row.id as string,
      handle: (row.handle as string) ?? "",
      displayName: (row.display_name as string) || "",
      email: (row.email as string) || "",
      avatarUrl: (row.avatar_url as string | null) ?? null,
      bio: (row.bio as string) || "",
      skills: (row.skills as string[]) ?? [],
      needs: (row.needs as string[]) ?? [],
      walletAddress: (row.wallet_address as string | null) ?? null,
      rolePreference: (row.role_preference ?? "both") as UserProfile["rolePreference"],
      // DEV-783 step 9. Read the real column rather than defaulting to true —
      // this row feeds the admin user detail, and hardcoding it here would
      // report every worker as opted in. Mirrors SupabaseUserService.mapProfileRow.
      recurringMatch: (row.recurring_match as boolean | null) ?? true,
      activityLevel: (row.activity_level ?? "casual") as ActivityLevel,
      preferredCurrency: (row.preferred_currency ?? "USDT") as UserProfile["preferredCurrency"],
      preferredLanguage: toSupportedLanguage(row.preferred_language),
      role: (row.role ?? "user") as UserRole,
      tier: toUserTier(row.tier),
      tierSetAt: (row.tier_set_at as string | null) ?? null,
      tierSetByName,
      reputation: {
        overall: Number(row.reputation_overall ?? 0),
        completionRate: Number(row.reputation_completion ?? 0),
        qualityScore: Number(row.reputation_quality ?? 0),
        speedScore: Number(row.reputation_speed ?? 0),
        reliability: Number(row.reputation_reliability ?? 0),
      },
      locationCountry: (row.location_country as string | null) ?? null,
      locationCity: (row.location_city as string | null) ?? null,
      createdAt: row.created_at as string,
    };

    // Financial aggregates (chain-scoped)
    // 1. Quests created by this user (for budgeted + paid)
    let createdQuestsQuery = supabase
      .from("quests")
      .select("id, total_budget_usd")
      .eq("creator_id", userId);
    if (chainId) createdQuestsQuery = createdQuestsQuery.eq("chain_id", chainId);
    const { data: createdQuests } = await createdQuestsQuery;

    const totalBudgeted = (createdQuests ?? []).reduce(
      (sum, q) => sum + Number(q.total_budget_usd ?? 0), 0,
    );

    // 2. Paid: sum of accepted proposal prices in quests created by this user
    const createdQuestIds = (createdQuests ?? []).map((q) => q.id);
    let totalPaid = 0;
    if (createdQuestIds.length > 0) {
      const { data: paidRows } = await supabase
        .from("proposals")
        .select("proposed_price")
        .in("quest_id", createdQuestIds)
        .eq("status", "accepted");
      totalPaid = (paidRows ?? []).reduce(
        (sum, r) => sum + Number(r.proposed_price ?? 0), 0,
      );
    }

    // 3. Earned: sum of accepted proposal prices where this user is the worker
    let earnedQuery = supabase
      .from("proposals")
      .select("proposed_price")
      .eq("worker_id", userId)
      .eq("status", "accepted");
    if (chainId) {
      const { data: chainQuests } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", chainId);
      const chainQuestIds = (chainQuests ?? []).map((q) => q.id);
      if (chainQuestIds.length === 0) {
        return {
          profile,
          walletAddress: (row.wallet_address as string | null) ?? null,
          chainRegistered: (row.chain_registered as number | null) ?? null,
          isOnboarded: (row.is_onboarded as boolean) ?? false,
          totalBudgeted, totalPaid, totalEarned: 0,
        };
      }
      earnedQuery = earnedQuery.in("quest_id", chainQuestIds);
    }
    const { data: earnedRows } = await earnedQuery;
    const totalEarned = (earnedRows ?? []).reduce(
      (sum, r) => sum + Number(r.proposed_price ?? 0), 0,
    );

    return {
      profile,
      walletAddress: (row.wallet_address as string | null) ?? null,
      chainRegistered: (row.chain_registered as number | null) ?? null,
      isOnboarded: (row.is_onboarded as boolean) ?? false,
      totalBudgeted,
      totalPaid,
      totalEarned,
    };
  }

  async getUserQuests(
    userId: string,
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<Quest>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;

    let query = supabase
      .from("quests")
      .select("*, tasks(id, status)", { count: "exact" })
      .eq("creator_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (filters.chainId) {
      query = query.eq("chain_id", filters.chainId);
    }
    if (filters.cursor) {
      const offset = parseInt(filters.cursor, 10);
      if (!Number.isNaN(offset)) query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list user quests: ${error.message}`);

    const rows = (data ?? []) as QuestRow[];
    const quests = rows.map(mapQuestRow);
    const total = count ?? quests.length;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const nextOffset = (Number.isNaN(currentOffset) ? 0 : currentOffset) + limit;

    return {
      data: quests,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async getUserTasks(
    userId: string,
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<Task>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;

    let questIdFilter: string[] | null = null;
    if (filters.chainId) {
      const { data: cq } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", filters.chainId);
      questIdFilter = (cq ?? []).map((q) => q.id);
      if (questIdFilter.length === 0) {
        return { data: [], total: 0, cursor: null, hasMore: false };
      }
    }

    let query = supabase
      .from("tasks")
      .select("*", { count: "exact" })
      .eq("assignee_id", userId)
      .order("updated_at", { ascending: false })
      .limit(limit);

    if (questIdFilter) {
      query = query.in("quest_id", questIdFilter);
    }
    if (filters.cursor) {
      const offset = parseInt(filters.cursor, 10);
      if (!Number.isNaN(offset)) query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list user tasks: ${error.message}`);

    const rows = (data ?? []) as TaskRow[];
    const tasks = rows.map(mapTaskRow);
    const total = count ?? tasks.length;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const nextOffset = (Number.isNaN(currentOffset) ? 0 : currentOffset) + limit;

    return {
      data: tasks,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async getUserProposals(
    userId: string,
    filters: AdminPaginationFilters,
  ): Promise<PaginatedResult<PriceProposal>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;

    let questIdFilter: string[] | null = null;
    if (filters.chainId) {
      const { data: cq } = await supabase
        .from("quests")
        .select("id")
        .eq("chain_id", filters.chainId);
      questIdFilter = (cq ?? []).map((q) => q.id);
      if (questIdFilter.length === 0) {
        return { data: [], total: 0, cursor: null, hasMore: false };
      }
    }

    let query = supabase
      .from("proposals")
      .select("*", { count: "exact" })
      .eq("worker_id", userId)
      .order("created_at", { ascending: false })
      .limit(limit);

    if (questIdFilter) {
      query = query.in("quest_id", questIdFilter);
    }
    if (filters.cursor) {
      const offset = parseInt(filters.cursor, 10);
      if (!Number.isNaN(offset)) query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`Failed to list user proposals: ${error.message}`);

    const rows = (data ?? []) as ProposalRow[];
    const proposals = rows.map(mapProposalRow);
    const total = count ?? proposals.length;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const nextOffset = (Number.isNaN(currentOffset) ? 0 : currentOffset) + limit;

    return {
      data: proposals,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  // ─── On-chain tx log ──────────────────────────────────
  //
  // Inserts are gated by an RLS policy requiring privy_did = requesting_user_id(),
  // so we don't call assertAdmin() here — any authenticated user logs their own
  // rows, and only admins can SELECT them.

  async logOnchainTx(row: OnchainTxInsert): Promise<void> {
    const { error } = await supabase.from("onchain_txs").insert({
      attempt_id: row.attemptId,
      chain_id: row.chainId,
      privy_did: row.privyDid,
      ui_resolved_signer: row.uiResolvedSigner,
      stored_wallet: row.storedWallet,
      registry_wallet: row.registryWallet,
      linked_wallets: row.linkedWallets,
      contract_key: row.contractKey,
      method: row.method,
      initiator_flow: row.initiatorFlow,
      related_quest_id: row.relatedQuestId,
      related_task_id: row.relatedTaskId,
      sponsored: row.sponsored,
      status: "pending",
    });
    if (error) throw new Error(`logOnchainTx: ${error.message}`);
  }

  async updateOnchainTxReceipt(
    attemptId: string,
    patch: OnchainReceiptPatch,
  ): Promise<void> {
    const updates: Record<string, unknown> = {};
    if (patch.txHash !== undefined) updates.tx_hash = patch.txHash;
    if (patch.receiptFrom !== undefined) updates.receipt_from = patch.receiptFrom;
    if (patch.status !== undefined) updates.status = patch.status;
    if (patch.blockNumber !== undefined) updates.block_number = patch.blockNumber;
    if (patch.gasUsed !== undefined) updates.gas_used = patch.gasUsed;
    if (patch.effectiveGasPrice !== undefined) updates.effective_gas_price = patch.effectiveGasPrice;
    if (patch.errorReason !== undefined) updates.error_reason = patch.errorReason;
    if (patch.confirmedAt !== undefined) updates.confirmed_at = patch.confirmedAt;

    if (Object.keys(updates).length === 0) return;

    const { error } = await supabase
      .from("onchain_txs")
      .update(updates)
      .eq("attempt_id", attemptId);
    if (error) throw new Error(`updateOnchainTxReceipt: ${error.message}`);
  }

  async listOnchainTxs(
    filters: OnchainTxFilters,
  ): Promise<PaginatedResult<OnchainTxRow>> {
    await assertAdmin();
    const limit = filters.limit ?? ADMIN_PAGE_SIZE;

    let query = supabase
      .from("onchain_txs")
      .select("*", { count: "exact" })
      .eq("chain_id", filters.chainId)
      .order("submitted_at", { ascending: false })
      .limit(limit);

    if (filters.status && filters.status !== "all") {
      query = query.eq("status", filters.status);
    }
    if (filters.contractKey && filters.contractKey !== "all") {
      query = query.eq("contract_key", filters.contractKey);
    }
    if (filters.method) {
      query = query.eq("method", filters.method);
    }
    if (filters.privyDid) {
      query = query.eq("privy_did", filters.privyDid);
    }
    if (filters.hashSearch) {
      query = query.ilike("tx_hash", `${filters.hashSearch}%`);
    }
    if (filters.addressPrefix) {
      const prefix = filters.addressPrefix.toLowerCase();
      query = query.or(
        `ui_resolved_signer.ilike.${prefix}%,receipt_from.ilike.${prefix}%,stored_wallet.ilike.${prefix}%,registry_wallet.ilike.${prefix}%`,
      );
    }
    if (filters.fromDate) {
      query = query.gte("submitted_at", filters.fromDate);
    }
    if (filters.toDate) {
      query = query.lte("submitted_at", filters.toDate);
    }
    if (filters.cursor) {
      const offset = parseInt(filters.cursor, 10);
      if (!Number.isNaN(offset)) query = query.range(offset, offset + limit - 1);
    }

    const { data, error, count } = await query;
    if (error) throw new Error(`listOnchainTxs: ${error.message}`);

    const rows = (data ?? []).map(mapOnchainTxRow);
    const total = count ?? rows.length;
    const currentOffset = filters.cursor ? parseInt(filters.cursor, 10) : 0;
    const nextOffset = (Number.isNaN(currentOffset) ? 0 : currentOffset) + limit;

    return {
      data: rows,
      total,
      cursor: nextOffset < total ? String(nextOffset) : null,
      hasMore: nextOffset < total,
    };
  }

  async getOnchainTx(attemptId: string): Promise<OnchainTxRow | null> {
    await assertAdmin();
    const { data, error } = await supabase
      .from("onchain_txs")
      .select("*")
      .eq("attempt_id", attemptId)
      .maybeSingle();
    if (error) throw new Error(`getOnchainTx: ${error.message}`);
    return data ? mapOnchainTxRow(data) : null;
  }

  async resolveChainQuestTitles(
    chainId: number,
    chainQuestIds: string[],
  ): Promise<Map<string, { id: string; title: string }>> {
    const map = new Map<string, { id: string; title: string }>();
    const unique = [...new Set(chainQuestIds.filter(Boolean))];
    if (unique.length === 0) return map;
    await assertAdmin();

    // chain_quest_id is stored as integer — cast string inputs before querying
    const asNums = unique
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (asNums.length === 0) return map;

    const { data, error } = await supabase
      .from("quests")
      .select("id, title, chain_quest_id")
      .eq("chain_id", chainId)
      .in("chain_quest_id", asNums);
    if (error) return map;

    for (const row of data ?? []) {
      map.set(String(row.chain_quest_id), { id: row.id, title: row.title });
    }
    return map;
  }

  async resolveChainTaskTitles(
    chainId: number,
    chainTaskIds: string[],
  ): Promise<Map<string, { id: string; title: string; questId: string }>> {
    const map = new Map<string, { id: string; title: string; questId: string }>();
    const unique = [...new Set(chainTaskIds.filter(Boolean))];
    if (unique.length === 0) return map;
    await assertAdmin();

    const asNums = unique
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n));
    if (asNums.length === 0) return map;

    // Join tasks → quests to filter by chain_id (tasks don't carry chain_id directly)
    const { data, error } = await supabase
      .from("tasks")
      .select("id, title, chain_task_id, quest_id, quests!inner(chain_id)")
      .in("chain_task_id", asNums)
      .eq("quests.chain_id", chainId);
    if (error) return map;

    for (const row of (data ?? []) as Array<{
      id: string;
      title: string;
      chain_task_id: number;
      quest_id: string;
    }>) {
      map.set(String(row.chain_task_id), {
        id: row.id,
        title: row.title,
        questId: row.quest_id,
      });
    }
    return map;
  }

  async resolveProfilesByWallet(
    addresses: string[],
  ): Promise<
    Map<string, { privyDid: string; displayName: string; email: string }>
  > {
    const map = new Map<
      string,
      { privyDid: string; displayName: string; email: string }
    >();
    const unique = [...new Set(addresses.filter(Boolean).map((a) => a.toLowerCase()))];
    if (unique.length === 0) return map;
    await assertAdmin();

    // wallet_address is stored as originally provided (mixed case). Use ilike-any
    // via `in` on lowercased column wouldn't work without a functional index, so
    // we select the full set and match client-side.
    const { data, error } = await supabase
      .from("profiles")
      .select("id, display_name, email, wallet_address")
      .not("wallet_address", "is", null);
    if (error) return map;

    for (const row of data ?? []) {
      const addr = (row.wallet_address as string | null)?.toLowerCase();
      if (!addr || !unique.includes(addr)) continue;
      map.set(addr, {
        privyDid: row.id,
        displayName: row.display_name || row.email || row.id,
        email: row.email || "",
      });
    }
    return map;
  }

  async getMapUsers(chainId?: number): Promise<AdminMapPoint[]> {
    const { data, error } = await supabase.rpc("admin_map_users", chainArgs(chainId));
    if (error) throw error;
    return (data as AdminMapPoint[]) ?? [];
  }

  async getMapQuests(chainId?: number): Promise<AdminMapPoint[]> {
    const { data, error } = await supabase.rpc("admin_map_quests", chainArgs(chainId));
    if (error) throw error;
    return (data as AdminMapPoint[]) ?? [];
  }

  async getMapProspects(chainId?: number): Promise<AdminMapPoint[]> {
    const { data, error } = await supabase.rpc("admin_map_prospects", chainArgs(chainId));
    if (error) throw error;
    return (data as AdminMapPoint[]) ?? [];
  }

  async getMapUserStats() {
    const { data, error } = await supabase.rpc("admin_map_user_stats");
    if (error) throw error;
    return (data as { rolePreference: { label: string; value: number }[]; topSkills: { label: string; value: number }[] }) ?? { rolePreference: [], topSkills: [] };
  }

  async getMapQuestStats(chainId?: number) {
    const { data, error } = await supabase.rpc("admin_map_quest_stats", chainArgs(chainId));
    if (error) throw error;
    return (data as { statusCounts: { label: string; value: number }[]; topCategories: { label: string; value: number }[] }) ?? { statusCounts: [], topCategories: [] };
  }

  async getMapNoLocationCounts(chainId?: number): Promise<{ users: number; quests: number; prospects: number }> {
    const { data, error } = await supabase.rpc("admin_map_no_location_counts", chainArgs(chainId));
    if (error) throw error;
    const d = data as { users: number; quests: number; prospects: number };
    return { users: d.users ?? 0, quests: d.quests ?? 0, prospects: d.prospects ?? 0 };
  }

  async listAgents(): Promise<AdminAgentSummary[]> {
    const { data, error } = await supabase.rpc("admin_list_agents");
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map((r) => ({
      agentId: r.agent_id as string,
      agentDid: r.agent_did as string,
      name: r.name as string,
      status: r.status as AdminAgentSummary["status"],
      ownerId: r.owner_id as string,
      ownerName: (r.owner_name as string | null) ?? "",
      createdAt: r.created_at as string,
      lastActivityAt: (r.last_activity_at as string | null) ?? null,
    }));
  }

  // ─── Quest status transitions (admin-only, DB-only) ───

  async adminCloseQuest(questId: string, reason?: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_close_quest", {
      p_id: questId,
      p_reason: reason ?? null,
    });
    if (error) throw error;
  }

  async listUserNotificationChannels(userId: string): Promise<AdminNotificationChannel[]> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_list_notification_channels", {
      p_user_id: userId,
    });
    if (error) throw error;

    return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      id: row.id as string,
      provider: row.provider as string,
      displayIdentifier: (row.display_identifier as string | null) ?? null,
      optedIn: Boolean(row.opted_in),
      verifiedAt: (row.verified_at as string | null) ?? null,
      revokedAt: (row.revoked_at as string | null) ?? null,
      revokedReason: (row.revoked_reason as string | null) ?? null,
      createdAt: row.created_at as string,
      updatedAt: row.updated_at as string,
    }));
  }

  async adminRevokeChannel(contactId: string): Promise<AdminChannelMutationResult> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_revoke_channel", {
      p_contact_id: contactId,
    });
    if (error) throw error;
    return data as AdminChannelMutationResult;
  }

  async adminRestoreChannel(contactId: string): Promise<AdminChannelMutationResult> {
    await assertAdmin();
    const { data, error } = await supabase.rpc("admin_restore_channel", {
      p_contact_id: contactId,
    });
    if (error) throw error;
    return data as AdminChannelMutationResult;
  }

  async adminCancelQuest(questId: string, reason?: string): Promise<void> {
    await assertAdmin();
    const { error } = await supabase.rpc("admin_cancel_quest", {
      p_id: questId,
      p_reason: reason ?? null,
    });
    if (error) throw error;
  }
}

// ─── Helpers ────────────────────────────────────────────

/**
 * Adds profiles.tier to a page of the Users list so it can be switched inline.
 * A second read rather than a new column on admin_list_users_by_chain, so no
 * migration is needed. A failed read leaves tier undefined; the list must
 * not break over a column it only decorates.
 */
async function withTiers(users: AdminUserSummary[]): Promise<AdminUserSummary[]> {
  if (users.length === 0) return users;
  const { data, error } = await supabase
    .from("profiles")
    .select("id, tier")
    .in("id", users.map((u) => u.id));
  if (error || !data) return users;
  const tiers = new Map((data as { id: string; tier: unknown }[]).map((r) => [r.id, toUserTier(r.tier)]));
  return users.map((u) => ({ ...u, tier: tiers.get(u.id) }));
}

function chainArgs(chainId?: number) {
  return chainId !== undefined ? { p_chain_id: chainId } : {};
}

interface OnchainTxDbRow {
  attempt_id: string;
  chain_id: number;
  tx_hash: string | null;
  privy_did: string;
  ui_resolved_signer: string;
  stored_wallet: string | null;
  registry_wallet: string | null;
  linked_wallets: OnchainLinkedWallet[] | null;
  receipt_from: string | null;
  contract_key: string;
  method: string;
  initiator_flow: string | null;
  related_quest_id: string | null;
  related_task_id: string | null;
  sponsored: boolean;
  status: string;
  block_number: number | null;
  gas_used: number | null;
  effective_gas_price: string | null;
  error_reason: string | null;
  submitted_at: string;
  confirmed_at: string | null;
}

function mapOnchainTxRow(row: OnchainTxDbRow): OnchainTxRow {
  return {
    attemptId: row.attempt_id,
    chainId: row.chain_id,
    txHash: row.tx_hash,
    privyDid: row.privy_did,
    uiResolvedSigner: row.ui_resolved_signer,
    storedWallet: row.stored_wallet,
    registryWallet: row.registry_wallet,
    linkedWallets: row.linked_wallets,
    receiptFrom: row.receipt_from,
    contractKey: row.contract_key as OnchainContractKey,
    method: row.method,
    initiatorFlow: row.initiator_flow,
    relatedQuestId: row.related_quest_id,
    relatedTaskId: row.related_task_id,
    sponsored: row.sponsored,
    status: row.status as OnchainTxStatus,
    blockNumber: row.block_number,
    gasUsed: row.gas_used,
    effectiveGasPrice: row.effective_gas_price,
    errorReason: row.error_reason,
    submittedAt: row.submitted_at,
    confirmedAt: row.confirmed_at,
  };
}

function emptyKPIs(): AdminWorkItemKPIs {
  return {
    totalQuests: 0,
    activeQuests: 0,
    totalTasks: 0,
    openTasks: 0,
    unassignedTasks: 0,
    staleTasks: 0,
    pendingReviewTasks: 0,
    disputedTasks: 0,
    completionRate: 0,
    totalUsers: 0,
    activeUsers: 0,
  };
}
